import { Component, Inject, Input, OnChanges, OnInit } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { ContentService, HrefResolverService } from '@blk/cwp-ng-framework';
import { DzModalService } from '@blk/daisy/modules/modals';
import { AdvisoryFeeChangeInfoDialogComponent, AdvisoryFeeUtil } from '@ddt-web/advisory-fee';
import { APP_STORE_CONFIG_TOKEN, ScenarioTesterDCR, ViewCategory } from '@ddt-web/config';
import { InjectConfig, PaConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig, CellData, ComponentData, DataServiceApiConstants, NumberFormatter, TableData } from '@ddt-web/data-service';
import { DialogService } from '@ddt-web/dialog';
import { Memoize } from '@ddt-web/generic-utils';
import { ApplicationStateStoreProxyService, PerformanceState } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { UrlBuilder, UrlService } from '@ddt-web/url';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as flatten from 'lodash/flatten';
import * as get from 'lodash/get';
import * as isNaN from 'lodash/isNaN';
import * as isNumber from 'lodash/isNumber';
import * as toNumber from 'lodash/toNumber';
import { first, tap } from 'rxjs/operators';

import { ScenarioAnalysisDollarValueFormatter, ScenarioAnalysisNumberFormatter } from '../../formatters/scenario-analysis-formatters';
import { ScenarioCategories } from '../../models/scenarios.interface';
import { ScenarioService } from '../../services/scenario.service';
import { ScenarioCategoryUtil } from '../../utils/scenario-category.util';
import { CustomizeDollarAmountDialogComponent } from '../customize-dollar-amount-dialog';
import { ScenarioSelectionDialogComponent } from '../scenario-selection-dialog';


@UntilDestroy()
@Component({
	selector: 'sc-market-moves',
	templateUrl: './market-moves.component.html',
	styleUrls: ['./market-moves.component.scss']
})
export class MarketMovesComponent implements OnInit, OnChanges {
	@InjectConfig(PaConfigKey.SHOW_SCENARIO_TESTER_LINK) public readonly showScenarioTesterLink: boolean = true;
	@InjectConfig(SharedConfigKey.ENABLE_SCENARIO_ADVISORY_FEE) public readonly enableScenarioAdvisoryFee: boolean = false;
	@InjectConfig(SharedConfigKey.EnableCustomFee) public readonly isCustomFeeEnabled: boolean = false;

	@Input() public mode: string;
	@Input() public data: ComponentData;

	public modalScenarioData: any;
	public allScenarios: Array<CellData>;
	public selectedScenarios: Array<CellData>;
	public selectedScenarioKeys: Array<string> = [];
	public defaultScenarioKeys: Array<string> = [];
	public isAboutAnalysisVisible: boolean;
	public scenarioNameColumnClass: string;
	public scenarioValueColumnClass: string;
	public componentTitle: string;
	public asOfDate: string;
	public marketMovesTableData: TableData;
	public yieldTableData: TableData;
	public isYieldDialogVisible: boolean = false;
	public includeYield: boolean = false;
	public yieldTitle: string;
	public yieldInfo: string;
	public dashValue: string;
	public containsTransitionPortfolio: boolean = false;
	public showDollarAmountAsDefault: boolean = false;
	public defaultAumValue: number;
	public categorisedSelectedScenarios: ScenarioCategories;
	public customAdvisoryFee: number;

	public ViewCategory = ViewCategory;

	public scenarioCategories = ['interest', 'bonds', 'commodities', 'equities', 'past'];

	private categorisedScenarioStrings: ScenarioCategories;

	constructor(
		protected contentService: ContentService,
		private storeService: StoreService,
		private hrefResolver: HrefResolverService,
		private urlService: UrlService,
		private dialogService: DialogService,
		private scenarioService: ScenarioService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private modalService: DzModalService,
		@Inject(APP_STORE_CONFIG_TOKEN) private storeConfig: AppStoreConfig
	) { }

	public ngOnInit(): void {
		this.componentTitle = this.contentService.get('SCENARIO_TESTER_TITLE', ScenarioTesterDCR.TITLE);
		this.storeService.selectAll(this.storeConfig.reportConfig)
			.pipe(untilDestroyed(this))
			.subscribe(data => {
				this.includeYield = get(data, 'includeScenarioYield');
				this.showDollarAmountAsDefault = get(data, 'showDollarAmountAsDefault', false);
			});
		this.yieldTitle = this.contentService.get(ScenarioTesterDCR.TWELVE_MONTH_TRAILING_YIELD_KEY, ScenarioTesterDCR.TWELVE_MONTH_TRAILING_YIELD_LABEL);
		this.yieldInfo = this.contentService.get(ScenarioTesterDCR.TWELVE_MONTH_TRAILING_YIELD_DESCRIPTION_KEY, ScenarioTesterDCR.TWELVE_MONTH_TRAILING_YIELD_DESCRIPTION);
		this.dashValue = NumberFormatter.DASH_VALUE;
		this.containsTransitionPortfolio = this.urlService.hasSpecificPortfolioId(DataServiceApiConstants.FTE_TRANSITION_ID);
		this.setUpDefaultAum();
		this.setupCustomAdvisoryFee();
	}

	public ngOnChanges(): void {
		if (this.data.success && this.data.portfolioData) {
			this.defaultScenarioKeys = this.contentService.get(ScenarioTesterDCR.DEFAULT_SCENARIOS_KEY, ScenarioTesterDCR.DEFAULT_SCENARIOS_VALUE).split(',');
			this.marketMovesTableData = this.data.getTabById('scenarios.marketMoves').tableData[0];
			this.yieldTableData = this.data.getTabById('scenarios.yield').tableData[0];
			this.asOfDate = this.data.portfolioData[0].find(column => column.config.id === 'asOfDate').formattedValue;
			if (this.marketMovesTableData) {
				const { scenarioNameColumnClass, scenarioValueColumnClass } = this.scenarioService.assignColumnClasses(this.marketMovesTableData.headersX);
				this.scenarioNameColumnClass = scenarioNameColumnClass;
				this.scenarioValueColumnClass = scenarioValueColumnClass;
				this.allScenarios = this.marketMovesTableData.headersY;
			}

			this.updateSelectedScenarios();
			this.updateCategorisedSelectedScenarios();
			this.updateDefaultAumValue();
		}
	}

	private updateCategorisedSelectedScenarios(): void {
		this.categorisedScenarioStrings = ScenarioCategoryUtil.getScenarioCategories(this.contentService);
		this.categorisedSelectedScenarios = ScenarioCategoryUtil.categoriseScenarios(this.categorisedScenarioStrings, this.selectedScenarios);
	}

	private updateSelectedScenarios(): void {
		this.storeService.selectAll(this.storeConfig.userScenarios)
			.pipe(untilDestroyed(this))
			.subscribe((userScenarios: any) => {
				if (userScenarios) {
					this.selectedScenarioKeys = userScenarios;
				}
			});
		this.selectedScenarios = this.scenarioService.convertKeysToSelectedScenarios(this.selectedScenarioKeys, this.defaultScenarioKeys, this.allScenarios);
	}

	private getPastScenarios(): Array<CellData> {
		const pastScenarioKeys = this.contentService.get(ScenarioTesterDCR.DEFAULT_SCENARIOS_KEY, ScenarioTesterDCR.PAST_SCENARIOS_VALUE).split(',');
		return this.allScenarios.filter(scenario => pastScenarioKeys.includes(scenario.rawValue.toString()));
	}

	public getCategoryName(category: string): string {
		return ScenarioCategoryUtil.getCategoryName(category, this.contentService);
	}

	@Memoize()
	public getImagePath(category: string): SafeUrl {
		return this.scenarioService.getImagePath(category);
	}

	// Note: this method looks like the one in ST, but there are differences!
	public getCellData(cell: CellData, index: number): string | number {
		// we need to set it to null if the raw value is null or undefined to display it as '-'
		// if data is missing then the formatted value will be '-' and the rawValue null
		const noYieldValue = cell.rawValue;
		const shouldIncludeScenarioAdvisoryFee = this.enableScenarioAdvisoryFee && this.isCustomFeeEnabled;

		if (noYieldValue && !isNaN(toNumber(noYieldValue))) {
			if (this.includeYield && this.yieldTableData) {
				let yieldValue = toNumber(get(this.yieldTableData, `cells[0][${index}].rawValue`));
				yieldValue = yieldValue !== undefined ? toNumber(noYieldValue) + yieldValue : toNumber(noYieldValue);

				return shouldIncludeScenarioAdvisoryFee ? AdvisoryFeeUtil.calculateValueWithScenarioAdvisoryFee(yieldValue, this.customAdvisoryFee, true) : yieldValue;
			}
			return shouldIncludeScenarioAdvisoryFee ?
				AdvisoryFeeUtil.calculateValueWithScenarioAdvisoryFee(toNumber(noYieldValue), this.customAdvisoryFee, false) :
				toNumber(noYieldValue);
		}

		return cell.formattedValue;
	}

	public getMarketMovesTableData(scenario: CellData, portfolioIndex: number): string | number {
		const scenarioIndex = this.marketMovesTableData.headersY.indexOf(scenario);
		if (scenarioIndex > -1) {
			const cell = this.marketMovesTableData.cells[scenarioIndex][portfolioIndex];
			return this.getCellData(cell, portfolioIndex);
		} else {
			return '-';
		}
	}

	public formatCellData(value: string): string {
		return new ScenarioAnalysisNumberFormatter().format(value);
	}

	/**
	 * Returns the formatted dollar value based on the provided percentage and default aum value.
	 * If the provided value is null, 'n/a' is returned;
	 * If the provided value is undefined, '-' is returned;
	 *
	 * @param {number} value the percentage value
	 * @param {boolean} [shouldAddValuePrefix=true]
	 * @returns {string} formatted dollar value
	*/
	public formatDollarValueCellData(value: number, shouldAddValuePrefix: boolean = true): string {
		return new ScenarioAnalysisDollarValueFormatter().format(value, this.defaultAumValue, shouldAddValuePrefix);
	}

	public goToScenarioTesterURL(): void {
		if (this.selectedScenarios) {
			const scenarioTesterUrl = this.hrefResolver.getResolvedUrl('scenarioTesterUrl');
			const scenariosQueryParam = this.getSelectedScenariosInCategories().map((scenario: CellData) => scenario.rawValue);
			const portfolioIdsQueryParam = this.data.portfolioData.map(portfolio => portfolio.field.id.rawValue);
			const url = UrlBuilder.construct().setPath(scenarioTesterUrl + '/analyse')
				.addParam('portfolios', portfolioIdsQueryParam)
				.addParam('stressTest', scenariosQueryParam)
				.build();
			window.open(url.toString());
		} else {
			console.error('There are no stress test scenarios available');
		}
	}

	public toggleScenarioYield(): void {
		this.includeYield = !this.includeYield;
		if (this.enableScenarioAdvisoryFee) {
			this.openAdvisoryFeeInfoDialog();
		}
		this.updateReportConfig();
	}

	public openScenarioSelectionDialog(): void {
		this.modalScenarioData = {
			allScenarios: this.allScenarios,
			selectedScenarios: this.selectedScenarios,
			pastScenarios: this.getPastScenarios()
		};
		const dialogRef = this.dialogService.open(ScenarioSelectionDialogComponent, { data: this.modalScenarioData });
		dialogRef.afterClosed.pipe(first()).subscribe(scenarioKeys => {
			if (scenarioKeys) {
				this.selectedScenarioKeys = scenarioKeys;
				this.updateSelectedScenarioKeys(this.selectedScenarioKeys);
			}
		});
	}

	public openCustomizeAmountDialog(): void {
		this.dialogService.open(CustomizeDollarAmountDialogComponent, {});
	}

	public updateShowDollarAmount(showDollarAmountAsDefault: boolean): void {
		this.showDollarAmountAsDefault = showDollarAmountAsDefault;
		this.updateReportConfig();
	}

	public updateReportConfig(): void {
		if (this.storeConfig.reportConfig) {
			this.storeService.upsertOne(this.storeConfig.reportConfig, {
				includeScenarioYield: this.includeYield,
				showDollarAmountAsDefault: this.showDollarAmountAsDefault
			});
		}
	}

	private openAdvisoryFeeInfoDialog() {
		this.modalService.open(AdvisoryFeeChangeInfoDialogComponent, {
			contentData: {
				includeScenarioYield: this.includeYield
			},
			style: { overflow: 'visible', width: '600px' }
		});
	}

	private setupCustomAdvisoryFee(): void {
		this.applicationStateStoreProxyService.getPerformanceState().pipe(
			untilDestroyed(this),
			tap((state: PerformanceState) => this.customAdvisoryFee = state.customAdvisoryFee)
		).subscribe();
	}

	private updateSelectedScenarioKeys(scenarioIds: Array<string>): void {
		this.storeService.loadOne(this.storeConfig.userScenarios, scenarioIds);
	}

	private setUpDefaultAum(): void {
		// Set up the default AUM value in the store
		if (!isNumber(this.defaultAumValue)) {
			const defaultAum = this.contentService.get('DEFAULT_GENERAL_AUM_VALUE', '100000');
			const defaultAumNumericValue = Number.parseFloat(defaultAum);
			this.storeService.loadOne(this.storeConfig.aumValueConfig, defaultAumNumericValue);
		}
	}

	/**
	 * Returns the aum value from the aumValueConfig store feature.
	 *
	 * @returns {number} the aum value
	*/
	public updateDefaultAumValue(): void {
		this.storeService.selectAll(this.storeConfig.aumValueConfig)
			.pipe(untilDestroyed(this))
			.subscribe((defaultAum: any) => {
				this.defaultAumValue = defaultAum;
			});
	}

	/**
	 * Returns the selected scenarios, categorised, in a single flat array.
	 *
	 * @returns {Array} the selected scenarios
	*/
	private getSelectedScenariosInCategories(): Array<CellData> {
		const selectedScenarios = this.scenarioCategories.map(category => this.categorisedSelectedScenarios[category]);

		return flatten(selectedScenarios);
	}
}
